import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Auth() {
  const { user, login, signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "signup";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav(user.is_admin ? "/admin" : "/dashboard");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        await signup(email.trim(), password);
        toast.success("Account created — let's set up your profile");
        nav("/wizard");
      } else {
        const u = await login(email.trim(), password, remember);
        nav(u?.is_admin ? "/admin" : "/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header minimal />
      <div className="lq-container page-enter" style={{ paddingTop: 60 }}>
        <div className="auth-card" data-testid="auth-card">
          <div className="auth-toggle">
            <button
              className={mode === "signup" ? "active" : ""}
              onClick={() => setMode("signup")}
              data-testid="mode-signup"
            >
              Sign Up
            </button>
            <button
              className={mode === "login" ? "active" : ""}
              onClick={() => setMode("login")}
              data-testid="mode-login"
            >
              Sign In
            </button>
          </div>

          <div className="eyebrow">— {mode === "signup" ? "Begin Your Archive" : "Welcome Back"}</div>
          <h2 className="serif" style={{ fontSize: 36, lineHeight: 1.05, margin: "8px 0 6px" }}>
            {mode === "signup" ? "Begin" : "Sign in"}
          </h2>
          <p style={{ color: "var(--text-mute)", fontSize: 13.5, marginBottom: 22 }}>
            {mode === "signup"
              ? "Your private workspace. Free, account-bound."
              : "Pick up exactly where you left off."}
          </p>

          <form onSubmit={submit}>
            <div style={{ marginBottom: 14 }}>
              <label className="label">Email</label>
              <input
                type="email"
                className="lq-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                required
                data-testid="auth-email"
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label className="label">Password</label>
              <input
                type="password"
                className="lq-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                required
                minLength={8}
                data-testid="auth-password"
              />
            </div>
            {mode === "signup" && (
              <div style={{ marginBottom: 14 }}>
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  className="lq-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="auth-confirm"
                />
              </div>
            )}

            {mode === "login" && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-mute)", fontSize: 13, marginBottom: 16 }}>
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ accentColor: "var(--accent)" }} />
                Remember me
              </label>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy} data-testid="auth-submit-btn">
              {busy ? "…" : (mode === "signup" ? "Create Account →" : "Sign In →")}
            </button>
          </form>

          <div style={{ marginTop: 18, textAlign: "center", fontSize: 13, color: "var(--text-mute)" }}>
            {mode === "signup" ? (
              <>By creating an account you'll move to a quick profile setup. Already have one?{" "}
                <button onClick={() => setMode("login")} style={{ background: "none", border: 0, color: "var(--accent)", cursor: "pointer", padding: 0, fontSize: 13 }} data-testid="switch-to-login">
                  Sign in
                </button></>
            ) : (
              <Link to="/forgot" style={{ color: "var(--accent)" }} data-testid="forgot-link">Forgot password?</Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
